import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Loader2, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  name: "",
  phone: "",
  email: "",
  product: "Sea Buckthorn Pure Pulp",
  message: "",
};

export const EnquiryModal = ({ open, onOpenChange, defaultProduct }) => {
  const [form, setForm] = useState({
    ...initialForm,
    product: defaultProduct || initialForm.product,
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [kitReminder, setKitReminder] = useState(false);

  // Show kit-reminder toggle for MAN 360 waitlist enquiries
  const isMan360 = /MAN\s*360/i.test(form.product || "");

  // Sync product field whenever the modal is (re)opened with a new defaultProduct
  useEffect(() => {
    if (open) {
      setForm((f) => ({ ...f, product: defaultProduct || initialForm.product }));
      setErrors({});
      setSuccess(false);
      setKitReminder(false);
    }
  }, [open, defaultProduct]);

  const handleChange = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((prev) => ({ ...prev, [k]: undefined }));
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Please share your name";
    if (!/^[+\d][\d\s\-]{5,20}$/.test(form.phone.trim())) errs.phone = "Enter a valid phone number";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errs.email = "Enter a valid email";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      // Compose the message — append kit-reminder note for MAN 360 waitlist
      const reminderNote =
        isMan360 && kitReminder
          ? "\n\n[Kit Reminder requested — please remind me to keep desi ghee ready before launch.]"
          : "";
      await axios.post(`${API}/enquiries`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        product: form.product,
        message: (form.message.trim() + reminderNote).trim(),
      });
      setSuccess(true);
      toast.success("Enquiry received. We'll be in touch shortly.");
      setForm({ ...initialForm, product: defaultProduct || initialForm.product });
      setKitReminder(false);
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again or WhatsApp us.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = (v) => {
    if (!v) {
      setTimeout(() => setSuccess(false), 250);
    }
    onOpenChange(v);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        data-testid="enquiry-modal"
        className="sm:max-w-[520px] bg-[var(--hk-ivory)] border-[var(--hk-border)] text-[var(--hk-ink)] rounded-[14px] p-8"
      >
        {!success ? (
          <>
            <DialogHeader className="space-y-2 text-left">
              <div className="label-eyebrow">Place an Enquiry</div>
              <DialogTitle className="serif text-[28px] leading-tight text-[var(--hk-ink)]">
                Order your HerbKranti ritual.
              </DialogTitle>
              <DialogDescription className="text-[14px] text-[var(--hk-ink-2)] leading-relaxed">
                Share your details — our team will reach out with pricing, availability and
                delivery information.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} noValidate className="mt-4 space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="name" className="text-[12px] tracking-[0.15em] uppercase text-[var(--hk-ink-3)]">
                  Name
                </Label>
                <Input
                  id="name"
                  data-testid="enquiry-name-input"
                  value={form.name}
                  onChange={handleChange("name")}
                  placeholder="Your full name"
                  className="bg-white border-[var(--hk-border)] focus-visible:ring-[var(--hk-orange)] rounded-md h-11"
                />
                {errors.name && <p className="text-xs text-red-600" data-testid="enquiry-name-error">{errors.name}</p>}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="phone" className="text-[12px] tracking-[0.15em] uppercase text-[var(--hk-ink-3)]">
                    Phone
                  </Label>
                  <Input
                    id="phone"
                    data-testid="enquiry-phone-input"
                    value={form.phone}
                    onChange={handleChange("phone")}
                    placeholder="+91 XXXXX XXXXX"
                    className="bg-white border-[var(--hk-border)] focus-visible:ring-[var(--hk-orange)] rounded-md h-11"
                  />
                  {errors.phone && <p className="text-xs text-red-600" data-testid="enquiry-phone-error">{errors.phone}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="email" className="text-[12px] tracking-[0.15em] uppercase text-[var(--hk-ink-3)]">
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="enquiry-email-input"
                    value={form.email}
                    onChange={handleChange("email")}
                    placeholder="you@example.com"
                    className="bg-white border-[var(--hk-border)] focus-visible:ring-[var(--hk-orange)] rounded-md h-11"
                  />
                  {errors.email && <p className="text-xs text-red-600" data-testid="enquiry-email-error">{errors.email}</p>}
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="product" className="text-[12px] tracking-[0.15em] uppercase text-[var(--hk-ink-3)]">
                  Product Interest
                </Label>
                <Input
                  id="product"
                  data-testid="enquiry-product-input"
                  value={form.product}
                  onChange={handleChange("product")}
                  className="bg-white border-[var(--hk-border)] focus-visible:ring-[var(--hk-orange)] rounded-md h-11"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="message" className="text-[12px] tracking-[0.15em] uppercase text-[var(--hk-ink-3)]">
                  Message (optional)
                </Label>
                <Textarea
                  id="message"
                  data-testid="enquiry-message-input"
                  value={form.message}
                  onChange={handleChange("message")}
                  rows={3}
                  placeholder="Any preferences or questions"
                  className="bg-white border-[var(--hk-border)] focus-visible:ring-[var(--hk-orange)] rounded-md"
                />
              </div>

              {/* MAN 360 — Kit Reminder toggle */}
              {isMan360 && (
                <label
                  htmlFor="kit-reminder"
                  className="flex items-start gap-3 p-4 rounded-[10px] border border-[var(--hk-border)] bg-white/70 cursor-pointer hover:border-[var(--hk-orange)]/60 transition-colors"
                  data-testid="enquiry-kit-reminder-wrap"
                >
                  <input
                    id="kit-reminder"
                    type="checkbox"
                    checked={kitReminder}
                    onChange={(e) => setKitReminder(e.target.checked)}
                    data-testid="enquiry-kit-reminder-checkbox"
                    className="mt-1 h-4 w-4 accent-[var(--hk-orange)] cursor-pointer"
                  />
                  <div className="flex-1">
                    <div className="serif text-[15px] text-[var(--hk-ink)] leading-snug" style={{ paddingBottom: "0.04em" }}>
                      Remind me to keep <span className="italic-accent text-[var(--hk-orange-deep)]">desi ghee</span> ready before launch.
                    </div>
                    <div className="mt-1 text-[12px] text-[var(--hk-ink-3)] leading-[1.6]">
                      We'll send a short, one-time nudge before MAN 360 goes live so your first
                      night ritual can begin without a run to the shop.
                    </div>
                  </div>
                </label>
              )}

              <DialogFooter className="mt-2">
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="enquiry-submit-btn"
                  className="hk-btn-primary w-full sm:w-auto justify-center disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {submitting && <Loader2 size={16} className="animate-spin" />}
                  {submitting ? "Sending" : "Send Enquiry"}
                </button>
              </DialogFooter>
            </form>
          </>
        ) : (
          <div
            className="py-6 flex flex-col items-start gap-3"
            data-testid="enquiry-success-state"
          >
            <div className="w-12 h-12 rounded-full bg-[var(--hk-warm-ivory)] flex items-center justify-center text-[var(--hk-orange-deep)] border border-[var(--hk-border)]">
              <CheckCircle2 size={22} />
            </div>
            <div className="label-eyebrow">Thank you</div>
            <h3 className="serif text-[26px] leading-tight text-[var(--hk-ink)]">
              Your enquiry has been received.
            </h3>
            <p className="text-[14px] leading-[1.75] text-[var(--hk-ink-2)]">
              A member of our team will reach out shortly. For faster response, message us on
              WhatsApp at{" "}
              <a
                className="text-[var(--hk-orange-deep)] underline underline-offset-4"
                href="https://wa.me/917249797972"
                target="_blank"
                rel="noreferrer"
              >
                +91 72497 97972
              </a>
              .
            </p>
            <button
              className="hk-btn-ghost mt-4"
              onClick={() => handleClose(false)}
              data-testid="enquiry-close-btn"
            >
              Close
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EnquiryModal;
